import * as classnames from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { RemoveIcon } from 'elc-icons';
import { ContentSpacing, Breakpoints, breakpoint } from 'elc-base-theme';
import { SliderView } from 'elc-slider';
import Slider from 'react-slick';
import { observer } from 'mobx-react';
import { BackgroundWhiteTheme } from '../../../theme/default-theme';
import { IAsset } from '../../domain/entities/Asset';
import {
    SLIDER_SETTINGS,
    SLIDER_FULL_SETTINGS,
    SLIDER_THUMB_SETTINGS
} from '../../constants/ProductZoomSliderSettings';
import { ProductImages } from 'elc-images';
import { Desktop } from 'elc-layouts';
import { Portal } from 'elc-portal';
import { lazyInject, serviceNames } from '../../../service-setup/diContainer';
import { ProductZoomViewController } from '../../controllers/ProductZoomViewController';
import { ProductZoomSlideItem } from './ProductZoomSlideItem';

interface IProductZoom {
    className?: string;
    largeImages: IAsset[];
    smallImages: IAsset[];
}

interface IProductZoomWrapper {
    showThumbSlider?: boolean;
    isZoomActive: boolean;
}

interface IZoomItems {
    currentZoomSlideIndex: number;
    items: IAsset[];
    isThumb?: boolean;
    isSlider?: boolean;
}

const ProductZoomWrapper = styled.section<IProductZoomWrapper>`
    align-items: flex-start;
    display: flex;
    height: 100%;
    justify-content: ${({ showThumbSlider }) => (showThumbSlider ? 'flex-start' : 'center')};
    left: 0;
    pointer-events: ${({ isZoomActive }) => (isZoomActive ? 'all' : 'none')};
    position: fixed;
    opacity: ${({ isZoomActive }) => (isZoomActive ? 1 : 0)};
    overflow: hidden;
    top: 0;
    visibility: ${({ isZoomActive }) => (isZoomActive ? 'visible' : 'hidden')};
    width: 100%;
    ${BackgroundWhiteTheme}
    ${breakpoint('desktop')`
        align-items: center;
    `};

    img {
        height: auto;
        margin: 0 auto;
        max-height: 100%;
        max-width: 100%;
        width: auto;

        &.elc-placeholder {
            height: 100%;
        }
    }
`;

export const CloseWrapper = styled.a`
    cursor: pointer;
    margin: ${ContentSpacing.space20};
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;
    ${breakpoint('desktop')`
        margin: ${ContentSpacing.space16} ${ContentSpacing.space48};
    `};

    svg {
        height: 100%;
        width: 100%;
    }

    [dir='rtl'] & {
        left: 0;
        right: inherit;
    }
`;

export const SliderListWrapper = styled.div`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    padding: 0 ${ContentSpacing.space10};
    position: relative;
    width: 100%;

    & > div {
        width: 100%;
    }
`;

export const SliderFullWrapper = styled.section`
    height: 100%;
    width: 100%;
    ${breakpoint('desktop')`
        height: 92vh;
        width: 60vw;
        transform: translateY(-2.5vh);
    `};

    & > div {
        padding: 0;
        ${breakpoint('desktop')`
            padding: 0 ${ContentSpacing.space10};
        `};
    }
`;

export const SliderThumbWrapper = styled.section`
    margin-right: 10vw;
    height: 60vh;
    width: 10vw;

    [dir='rtl'] & {
        margin: 0 0 0 10vw;
    }

    .elc-placeholder {
        width: 75%;
    }
`;

const ProductZoomClassNames = ['elc-product-zoom', 'js-product-zoom'];
const ProductZoomFullSliderClassNames = classnames([
    'elc-product-zoom-full-slider',
    'js-product-zoom-full-slider'
]);
const ProductZoomFullSliderViewClassNames = classnames([
    'elc-product-zoom-full-slider-view',
    'js-product-zoom-full-slider-view'
]);
const ProductZoomThumbSliderClassNames = classnames([
    'elc-product-zoom-thumb-slider',
    'js-product-zoom-thumb-slider'
]);
const ProductZoomThumbSliderViewClassNames = classnames([
    'elc-product-zoom-thumb-slider-view',
    'js-product-zoom-thumb-slider-view'
]);
const ProductZoomThumbListClassNames = classnames([
    'elc-product-zoom-thumb-list',
    'js-product-zoom-thumb-list'
]);

@observer
export class ProductZoom extends React.Component<IProductZoom> {
    @lazyInject(serviceNames.productZoomViewController)
    private productZoomViewController: ProductZoomViewController;

    private sliderFullRef: Slider;
    private sliderThumbRef: Slider;

    private slickGoTo = (index: number) => {
        const {
            setCurrentZoomSlideIndex,
            data: { currentZoomSlideIndex }
        } = this.productZoomViewController;
        const { slickGoTo = null } = this.sliderFullRef || {};

        if (setCurrentZoomSlideIndex && currentZoomSlideIndex !== index) {
            setCurrentZoomSlideIndex(index);
        }

        if (slickGoTo) {
            slickGoTo(index);
        }
    };

    private onReInit = () => {
        const {
            data: { currentZoomSlideIndex }
        } = this.productZoomViewController;

        const { slickGoTo = null } = this.sliderFullRef || {};
        if (slickGoTo) {
            slickGoTo(currentZoomSlideIndex);
        }
    };

    private getFullSlider = () => {
        const { largeImages } = this.props;
        const {
            data: { currentZoomSlideIndex },
            setCurrentZoomSlideIndex
        } = this.productZoomViewController;

        const sliderFullSettings = {
            ...SLIDER_FULL_SETTINGS,
            initialSlide: currentZoomSlideIndex,
            afterChange: setCurrentZoomSlideIndex,
            onReInit: this.onReInit
        };

        const fullItemList = this.getZoomItems({
            items: largeImages,
            isSlider: true,
            currentZoomSlideIndex: currentZoomSlideIndex
        });

        return (
            <SliderFullWrapper className={ProductZoomFullSliderClassNames}>
                <SliderView
                    asNavFor={this.sliderThumbRef}
                    className={ProductZoomFullSliderViewClassNames}
                    items={fullItemList}
                    innerRef={this.setSliderFullRef}
                    sliderSettings={sliderFullSettings}
                />
            </SliderFullWrapper>
        );
    };

    private getThumbSlider = () => {
        const { smallImages } = this.props;
        const {
            data: { currentZoomSlideIndex }
        } = this.productZoomViewController;
        const { length: thumbListLength } = smallImages;
        const hasThumbSliderNavigation = thumbListLength > SLIDER_SETTINGS.THUMB_DEFAULT_LENGTH;

        const thumbItemList = this.getZoomItems({
            items: smallImages,
            isThumb: true,
            isSlider: hasThumbSliderNavigation,
            currentZoomSlideIndex: currentZoomSlideIndex
        });

        const sliderThumbSettings = {
            ...SLIDER_THUMB_SETTINGS,
            initialSlide: currentZoomSlideIndex
        };

        return (
            <Desktop minWidth={Breakpoints.desktop}>
                {() => (
                    <SliderThumbWrapper className={ProductZoomThumbSliderClassNames}>
                        {hasThumbSliderNavigation ? (
                            <SliderView
                                asNavFor={this.sliderFullRef}
                                className={ProductZoomThumbSliderViewClassNames}
                                innerRef={this.setSliderThumbRef}
                                items={thumbItemList}
                                sliderSettings={sliderThumbSettings}
                            />
                        ) : (
                            <SliderListWrapper className={ProductZoomThumbListClassNames}>
                                {thumbItemList}
                            </SliderListWrapper>
                        )}
                    </SliderThumbWrapper>
                )}
            </Desktop>
        );
    };

    private getZoomItems = ({
        items,
        isThumb = false,
        isSlider = false,
        currentZoomSlideIndex
    }: IZoomItems) => {
        return items.map((item, index) => {
            const keyBase = isThumb ? 'zoom-item-thumb-' : 'zoom-item-full-';
            const isCurrent = currentZoomSlideIndex === index && !isSlider;
            const className = isCurrent ? 'current' : '';

            return (
                <ProductZoomSlideItem
                    key={`${keyBase}${index}`}
                    index={index}
                    className={className}
                    onClick={!isSlider ? this.slickGoTo : undefined}
                    isThumb={isThumb}
                    image={item}
                >
                    <ProductImages imgPath={item.src} altText={item.alt} />
                </ProductZoomSlideItem>
            );
        });
    };

    private setSliderFullRef = (slider: Slider) => {
        if (!this.sliderFullRef) {
            this.sliderFullRef = slider;
        }

        return this.sliderFullRef;
    };

    private setSliderThumbRef = (slider: Slider) => {
        if (!this.sliderThumbRef) {
            this.sliderThumbRef = slider;
        }

        return this.sliderThumbRef;
    };

    public render() {
        const { className, smallImages } = this.props;
        const {
            data: { isZoomActive },
            closeZoom
        } = this.productZoomViewController;
        const { length: thumbListLength } = smallImages;
        const showThumbSlider = thumbListLength > 1;

        return (
            <Portal targetSelector={'body'}>
                <ProductZoomWrapper
                    className={classnames(...ProductZoomClassNames, className)}
                    isZoomActive={isZoomActive}
                    showThumbSlider={showThumbSlider}
                >
                    <CloseWrapper onClick={closeZoom}>
                        <RemoveIcon />
                    </CloseWrapper>
                    {showThumbSlider && this.getThumbSlider()}
                    {this.getFullSlider()}
                </ProductZoomWrapper>
            </Portal>
        );
    }
}
